/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import config from "../config/studentsLists.config.json";

import { useGroupId } from "../../../pages/HoldersList/provider";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_HOLDER_LIST } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment";

import { DEFAULT_APP_CONFIG } from "../../../config/default.config";

import { templateReplacer } from "../../../helpers/template.replace";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetHoldersLisProps {
  offset?: number;
  size?: number;
  criteria?: any;
}

export interface IHoldersListMethods extends IPluginMethodMap {
  getHoldersListProps(args: any): Promise<any>;
}

export class HoldersListService implements IAgentPlugin {
  readonly methods: IHoldersListMethods;

  constructor() {
    this.methods = {
      getHoldersListProps: this.getHoldersListProps.bind(this),
    };
  }

  async getHoldersListProps(args: IGetHoldersLisProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { group } = useGroupId();
    try {
      if (client) {
        let criteria = {};
        if (group) {
          criteria = {
            group: group,
            role: config?.role,
            ...args?.criteria,
          };
        } else {
          criteria = {
            role: config?.role,
            ...args?.criteria,
          };
        }
        const res = await client.query({
          query: GET_HOLDER_LIST,
          variables: {
            input: {
              criteria: criteria,
              offset: args?.offset ?? 0,
              size: args?.size ?? 10,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          console.log(`table data`, res);
          let _rowData = this.formatUserTable(res?.data?.getListOfUser?.data);
          let _config = this.getTransformConfig(config);
          return {
            ..._config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getListOfUser?.data?.length <= 0,
            // studentGroup: holderGroups,
            activePage: res?.data?.getListOfUser?.currentPage,
            pageSize: res?.data?.getListOfUser?.pageSize,
            totalCount: res?.data?.getListOfUser?.totalCount,
            totalPages: res?.data?.getListOfUser?.totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      return {
        isEmpty: true,
      };
    }
  }

  private getTransformConfig(data:any){
    const _transConfig = templateReplacer(JSON.stringify(data), {holder_name:DEFAULT_APP_CONFIG?.platformSemantics?.semantics?.holder, holders_name:DEFAULT_APP_CONFIG?.platformSemantics?.semantics?.holders, holder_group:DEFAULT_APP_CONFIG?.platformSemantics?.HoldersGroups?.title});
    return JSON.parse(_transConfig);
}

  private formatUserTable(data: any) {
    let _rowData: any =
      Array.isArray(data) &&
      data.map((item: any) => {
        return {
          id: item?._id,
          userId: item?.userId,
          status: this.getFormattedStatus(item?.accountStatus, item?.suspended),
          name: `${item?.firstName} ${item?.lastName}`,
          email: item?.email,
          dateCreated: this.formatCreatedDate(item?.createdAt),
          group: item?.group?.name,
          image: item?.img,
          verified: item?.emailVerified,
          suspended: item?.suspended,
        };
      });

    return _rowData;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LLL");
    }
  }

  private getFormattedStatus(status: string, suspended: boolean) {
    if (suspended) {
      return "suspended";
    } else {
      switch (status) {
        case "USER_ADDED":
          return "created";
        case "INVITED":
          return "invited";
        case "EMAIL_VERIFIED":
          return "verified";
        case "ACC_CREATED":
          return "active";
        case "WALLET_REGISTERED":
          return "active";
        case "SUSPENDED":
          return "suspended";
        default:
          return "-";
      }
    }
  }
}
